import {
  HandshakeOutlined,
  HouseOutlined,
  PercentOutlined,
} from "@mui/icons-material";
import CurrencyExchangeOutlined from "@mui/icons-material/CurrencyExchangeOutlined";
import DoDisturbOutlined from "@mui/icons-material/DoDisturbOutlined";
import GavelOutlined from "@mui/icons-material/GavelOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import PointOfSaleOutlined from "@mui/icons-material/PointOfSaleOutlined";
import { Box, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import { isNil } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useFormState } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import RffDollarField from "../../../../components/finalForm/inputs/RffDollarField";
import RffFormChanges from "../../../../components/finalForm/RffFormChanges";
import RffDataRow from "../../../../components/finalForm/shared/RffDataRow";
import SearchInput from "../../../../components/inputs/SearchInput";
import Flx from "../../../../components/layout/Flx";
import ScreenContent from "../../../../components/layout/ScreenContent";
import TitledHeader from "../../../../components/layout/TitledHeader";
import BasicModal from "../../../../components/modals/BasicModal";
import TabPanel from "../../../../components/navigation/TabPanel";
import ToggleTabSwitcher from "../../../../components/navigation/ToggleTabSwitcher";
import Txt from "../../../../components/typography/Txt";
import TitledCard from "../../../../components/ui/TitledCard";
import { selectOptionsAmortizationType } from "../../../../constants/selectOptions/selectOptionsAmortizationType";
import { selectOptionsInterestCalcType } from "../../../../constants/selectOptions/selectOptionsInterestCalcType";
import { selectOptionsLoanProductType } from "../../../../constants/selectOptions/selectOptionsLoanProductType";
import {
  getExitStrategyOptions,
  isExitStrategyHidden,
} from "../../../../constants/selectOptions/selectOptionsExitStrategy";
import { selectOptionsLoanPurpose } from "../../../../constants/selectOptions/selectOptionsLoanPurpose";
import { selectOptionsPipelineType } from "../../../../constants/selectOptions/selectOptionsPipelineType";
import { selectOptionsPrepaymentPenaltyStructure } from "../../../../constants/selectOptions/selectOptionsPrepaymentPenaltyStructure";
import { selectOptionsVomPaymentHistory } from "../../../../constants/selectOptions/selectOptionsVomPaymentHistory";
import LoanDrilldownChangesForm from "../../../../forms/LoanDrilldownChangesForm";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { formatDollar } from "../../../../utils/numbers/formatDollar";
import LoanDrilldownButtonMarkLoan from "../../buttons/LoanDrilldownButtonMarkLoan";
import { selectOptionsWireStatus } from "../../../../constants/selectOptions/selectOptionsWireStatus";
import LoanCounterpartyCard from "./LoanCounterpartyCard";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";
import {
  DELAYED_PURCHASE_FIELD,
  DELAYED_PURCHASE_LABEL,
  canBeDelayedPurchase,
} from "../../../../utils/loans/delayedPurchase";
// your existing select options

// For any new options you need, e.g. amortizationType, interestCalcType, etc.

const LOAN_INFO_TAB_TYPES = {
  ALL: "All",
  LOAN_META: "Loan Meta",
  LOAN_TERMS: "Loan Terms",
  REFINANCE_INFO: "Refinance Info",
  INTEREST_RESERVES: "Interest Reserves",
  PREPAYMENT: "Prepayment",
  LOAN_CLOSING: "Loan Closing",
  DEAD_DEAL: "Dead Deal",
};
const LoanDrilldownDetailsTab = () => {
  const [quickFilter, setQuickFilter] = useState("");
  const theme = useTheme();
  const [tab, setTab] = useState(LOAN_INFO_TAB_TYPES.ALL);

  return (
    <>
      <TitledHeader
        title={"Loan Information"}
        sx={{ borderBottom: `1px solid ${theme.palette.divider}`, pb: 0.5 }}
      >
        <ToggleTabSwitcher
          value={tab}
          onChange={setTab}
          sx={{ mt: 1 }}
          tabs={Object.values(LOAN_INFO_TAB_TYPES)}
        />
      </TitledHeader>

      <ScreenContent
        sx={{
          flexGrow: 1,
          width: "100%",
          // maxWidth: "1880px",
          maxWidth: "1440px",

          margin: "0px auto",
          pb: 14,
          fieldset: {
            border: "none",
          },
        }}
      >
        <Flx ac sx={{ gap: 3, mb: 2 }}>
          <SearchInput
            value={quickFilter}
            onChange={setQuickFilter}
            // fullWidth
            // width={"250px"}
            variant="paper"
            rounded
            placeholder={"Search in loan..."}
            sx={{ flexGrow: 1 }}
          />

          {/* <LoanContentsSection quickFilter={quickFilter} /> */}
          <Flx ac sx={{ gap: 1 }}>
            <LoanDrilldownButtonMarkLoan type="Dead" />
            <LoanDrilldownButtonMarkLoan type="Closed" />
          </Flx>
        </Flx>
        <LoanDrilldownChangesForm
          checkForPropertyChanges={["interestReserves"]}
        >
          <LoanContentsSection tab={tab} quickFilter={quickFilter} />
        </LoanDrilldownChangesForm>
      </ScreenContent>
    </>
  );
};

const LoanContentsSection = ({ quickFilter, tab }) => {
  const gap = 2;
  // Cards that don't belong to a category sub-tab (general loan references) sit
  // full-width UNDERNEATH the grid, shown on the "All" view.
  const showBottomCards = tab === LOAN_INFO_TAB_TYPES.ALL;
  return (
    <Flx column gap={gap}>
      <FlxGrid
        width="480px"
        gap={gap}
        sx={{
          // Borderless input fieldsets in Loan Info — drop the outlined input
          // notched-outline border in resting + hover states (focus border kept
          // so the active field is still indicated).
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "transparent",
          },
        }}
      >
        {tab === LOAN_INFO_TAB_TYPES.ALL ||
        tab === LOAN_INFO_TAB_TYPES.LOAN_META ||
        tab === LOAN_INFO_TAB_TYPES.LOAN_TERMS ? (
          <Flx column gap={gap}>
            <TabPanel
              value={LOAN_INFO_TAB_TYPES.LOAN_META}
              tabValue={tab}
              forceDisplay={LOAN_INFO_TAB_TYPES.ALL}
            >
              <LoanMetaCard quickFilter={quickFilter} />
            </TabPanel>
            <TabPanel
              value={LOAN_INFO_TAB_TYPES.LOAN_TERMS}
              tabValue={tab}
              forceDisplay={LOAN_INFO_TAB_TYPES.ALL}
            >
              <LoanTermsCard quickFilter={quickFilter} />
            </TabPanel>
          </Flx>
        ) : null}
        {tab === LOAN_INFO_TAB_TYPES.ALL ||
        tab === LOAN_INFO_TAB_TYPES.REFINANCE_INFO ||
        tab === LOAN_INFO_TAB_TYPES.PREPAYMENT ||
        tab === LOAN_INFO_TAB_TYPES.INTEREST_RESERVES ||
        tab === LOAN_INFO_TAB_TYPES.LOAN_CLOSING ||
        tab === LOAN_INFO_TAB_TYPES.DEAD_DEAL ? (
          <Flx column gap={gap}>
            <TabPanel
              value={LOAN_INFO_TAB_TYPES.INTEREST_RESERVES}
              tabValue={tab}
              forceDisplay={LOAN_INFO_TAB_TYPES.ALL}
            >
              <InterestReservesCard quickFilter={quickFilter} />
            </TabPanel>
            <TabPanel
              value={LOAN_INFO_TAB_TYPES.REFINANCE_INFO}
              tabValue={tab}
              forceDisplay={LOAN_INFO_TAB_TYPES.ALL}
            >
              <RefinanceInfoCard quickFilter={quickFilter} />
            </TabPanel>
            <TabPanel
              value={LOAN_INFO_TAB_TYPES.PREPAYMENT}
              tabValue={tab}
              forceDisplay={LOAN_INFO_TAB_TYPES.ALL}
            >
              <PrepaymentCard quickFilter={quickFilter} />
            </TabPanel>
            <TabPanel
              value={LOAN_INFO_TAB_TYPES.LOAN_CLOSING}
              tabValue={tab}
              forceDisplay={LOAN_INFO_TAB_TYPES.ALL}
            >
              <LoanClosingCard quickFilter={quickFilter} />
            </TabPanel>
            <TabPanel
              value={LOAN_INFO_TAB_TYPES.DEAD_DEAL}
              tabValue={tab}
              forceDisplay={LOAN_INFO_TAB_TYPES.ALL}
            >
              <DeadDealCard quickFilter={quickFilter} />
            </TabPanel>
          </Flx>
        ) : null}
      </FlxGrid>
      {showBottomCards && (
        <FlxGrid
          width="480px"
          gap={gap}
          sx={{
            // Borderless input fieldsets in Loan Info — drop the outlined input
            // notched-outline border in resting + hover states (focus border kept
            // so the active field is still indicated).
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "transparent",
            },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "transparent",
            },
          }}
        >
          <LoanCounterpartyCard />
          <LoanExternalLinksCard quickFilter={quickFilter} />
        </FlxGrid>
      )}
    </Flx>
  );
};

// External URL links the loan model accepts (UnderwritingBaseModel). Rendered as
// a standard editable data card so it saves the SAME way as the other cards
// (through the surrounding LoanDrilldownChangesForm → update_loan_data).
const LoanExternalLinksCard = ({ quickFilter }) => {
  const fields = useMemo(
    () => [
      {
        name: "box_loan_folder_url",
        label: "Box — Loan Folder URL",
        type: "text",
      },
      {
        name: "box_correspondent_folder_url",
        label: "Box — Correspondent Folder URL",
        type: "text",
      },
    ],
    [],
  );
  return (
    <FilterableLoanDataCard
      title="External Links"
      fields={fields}
      icon={<LinkOutlined className="thin" />}
      quickFilter={quickFilter}
    />
  );
};

const LoanMetaCard = ({ quickFilter }) => {
  // Live form values so Exit Strategy options track the selected Product Type.
  const { values } = useFormState({ subscription: { values: true } });
  const fields = useMemo(
    () => [
      {
        name: "createDateTime",
        label: "Create Date Time",
        type: "date",
        disabled: true,
      },
      {
        name: "updateDateTime",
        label: "Update Date Time",
        type: "date",
        disabled: true,
      },
      {
        name: "loanNumber",
        label: "Loan Number",
        disabled: true,
      },
      {
        name: "sync_loan_id",
        label: "External Loan Sync ID",
        // disabled: true,
      },
      {
        name: "loanName",
        label: "Loan Name",
        type: "text",
      },

      {
        name: "loanProductType",
        label: "Loan Product Type",
        type: "select",
        options: selectOptionsLoanProductType,
      },
      {
        name: "loanPurpose",
        label: "Loan Purpose",
        type: "select",
        options: selectOptionsLoanPurpose,
      },
      // Delayed purchase is a flag on a cash-out refinance (REQ-106) — offered
      // only there; the backend clears it on any other purpose.
      ...(canBeDelayedPurchase(values?.loanPurpose)
        ? [
            {
              name: DELAYED_PURCHASE_FIELD,
              label: DELAYED_PURCHASE_LABEL,
              type: "booleanToggle",
            },
          ]
        : []),
      // Borrower classification (loan root-level). Both are read-only here: the
      // override needs a justification note, so it is set only through the
      // Override classification modal on the Loan Info tab.
      {
        name: "borrower_classification_calculated",
        label: "Borrower Classification (Calculated)",
        type: "text",
        disabled: true,
      },
      {
        name: "borrower_classification_override",
        label: "Borrower Classification Override",
        type: "text",
        disabled: true,
      },

      // {
      //   name: "isBalanceSheetClosing",
      //   label: "Is Balance Sheet Closing?",
      //   type: "booleanToggle",
      // },
      {
        name: "termMonths",
        label: "Term Months",
        type: "number",
      },
      // Exit Strategy is hidden entirely for product types where it doesn't
      // apply (DSCR / Other); otherwise options are scoped to the product type
      // and any existing out-of-list value is appended so it stays visible.
      ...(isExitStrategyHidden(values?.loanProductType)
        ? []
        : [
            {
              name: "exitStrategy",
              label: "Exit Strategy",
              type: "select",
              options: getExitStrategyOptions(
                values?.loanProductType,
                values?.exitStrategy,
              ),
            },
          ]),
      {
        name: "dscr",
        label: "The DSCR for the loan as measured by counterparty",
        type: "float",
      },
      {
        name: "totalInPlaceRents",
        label: "Total In Place Rents",
        type: "dollar",
      },
      {
        name: "totalMarketRents",
        label: "Total Market Rents",
        type: "dollar",
      },
      {
        name: "pipeline",
        label: "Current Pipeline",
        type: "select",
        options: selectOptionsPipelineType,
      },
      // {
      //   name: "assetId",
      //   label: "Asset Id",
      //   type: "text",
      //   disabled: true,
      // },
    ],
    [values?.loanProductType, values?.exitStrategy, values?.loanPurpose],
  );

  return (
    <FilterableLoanDataCard
      title="Loan Meta"
      fields={fields}
      icon={<InfoOutlined className="thin" />}
      quickFilter={quickFilter}
    />
  );
};

const LoanTermsCard = ({ quickFilter }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const fields = useMemo(() => {
    const baseFields = [
      {
        name: "baseLoanAmount",
        label: "Base Loan Amount",
        type: "dollar",
      },
      {
        name: "totalHoldback",
        label: "Total Holdback",
        type: "dollar",
      },
      {
        name: "requiredInterestCoverage",
        label: "Required Interest Coverage",
        type: "dollar",
      },
      {
        name: "requiredLiquidity",
        label: "Required Liquidity",
        type: "dollar",
      },
      {
        name: "amortizationType",
        label: "Amortization Type",
        type: "select",
        options: selectOptionsAmortizationType,
      },
      {
        name: "totalInterestReserves",
        label: "Total Interest Reserves",
        type: "dollar",
        disabled: true,
      },
      // {
      //   name: "originationDate",
      //   label: "Origination Date",
      //   type: "date",
      // },
      {
        name: "fundedDate",
        label: "Funded Date",
        type: "date",
      },
      {
        name: "maturityDate",
        label: "Maturity Date",
        type: "date",
      },
      {
        name: "expectedClosingDate",
        label: "Expected Closing Date",
        type: "date",
      },
      // {
      //   name: "applicationFeeDate",
      //   label: "Application Fee Date",
      //   type: "date",
      // },

      {
        name: "interestRate",
        label: "Interest Rate",
        type: "percent",
      },
      {
        name: "discountPoints",
        label: "Discount Points",
        type: "percent",
      },
      {
        name: "originationPoints",
        label: "Origination Points",
        type: "percent",
      },
      {
        name: "exitFeePercent",
        label: "Exit Fee Percent",
        type: "percent",
      },
      {
        name: "floatingRateLoan",
        label: "Floating Rate Loan?",
        type: "booleanCheckbox",
      },
    ];

    let fields = [...baseFields];
    if (loanDrilldown?.floatingRateLoan) {
      fields = [
        ...baseFields,

        {
          name: "floatingRateLoanBaseRate",
          label: "Floating Rate Base Rate",
          type: "percent",
          hidden: true,
        },
        {
          name: "floatingRateLoanFloorRate",
          label: "Floating Rate Floor Rate",
          type: "percent",
        },
        {
          name: "floatingRateLoanCapRate",
          label: "Floating Rate Cap Rate",
          type: "percent",
        },
        {
          name: "floatingRateLoanRateIndex",
          label: "Floating Rate Index",
          type: "text",
        },
        {
          name: "floatingRateLoanRateMargin",
          label: "Floating Rate Margin",
          type: "percent",
        },
      ];
    }

    return fields;
  }, [loanDrilldown]);

  return (
    <FilterableLoanDataCard
      title="Loan Terms"
      fields={fields}
      icon={<GavelOutlined className="thin" />}
      quickFilter={quickFilter}
    />
  );
};

const InterestReservesCard = ({ quickFilter }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  return (
    <FilterableLoanDataCard
      title="Interest Reserves By Property"
      // fields={fields}
      headerEndContent={
        <Flx
          g={1}
          ac
          sx={{
            background: grey[200],
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "0.875rem",
            fontWeight: 500,
            color: grey[800],
          }}
        >
          <Txt>Total Interest Reserves:</Txt>
          <Txt bold> {formatDollar(loanDrilldown?.totalInterestReserves)}</Txt>
        </Flx>
      }
      icon={<PercentOutlined className="thin" />}
      quickFilter={quickFilter}
    >
      <SubjectPropertyInterestReservesTable />
    </FilterableLoanDataCard>
  );
};

const SubjectPropertyInterestReservesTable = ({ children }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const [property, setProperty] = useState(null);

  const onEditClick = useCallback((p) => {
    setProperty(p);
  }, []);

  const sharedTxtStyles = useMemo(() => {
    return {
      fontWeight: 400,
      lineHeight: 1.5,
      fontSize: "12px",
      color: "#33475b",
      padding: "6.4px",
    };
  }, []);
  return (
    <>
      <Flx column sx={{ borderTop: "1px dashed #dde0e4" }}>
        {loanDrilldown?.subjectProperties?.map((prop, index) => {
          return (
            <FlxGrid
              gridTemplateColumns={"1fr 180px"}
              sx={{ borderBottom: "1px dashed #dde0e4" }}
            >
              <Flx fw ac sx={{ height: "100%" }}>
                <Txt sx={sharedTxtStyles}>
                  {addressLabel(prop.address) || prop._id}
                </Txt>
              </Flx>
              <Txt
                onClick={() => onEditClick(prop)}
                sx={{
                  ...sharedTxtStyles,
                  p: "8.5px 14px",
                  background: "#e3f2fd35",
                  border: "1px solid #e3f2fdff",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {formatDollar(prop?.interestReserves || 0)}
              </Txt>
            </FlxGrid>
          );
        })}
      </Flx>
      {isNil(property) ? null : (
        <UpdatePropertyModal
          property={property}
          onClose={() => setProperty(null)}
        />
      )}
    </>
  );
};

const UpdatePropertyModal = ({
  onClose,
  loanId,

  property,
  onSuccessFn,
}) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { updateSubjectProperty, loading } = useUnderwritingHook();
  const dispatch = useDispatch();

  const handleOnClose = useCallback(() => {
    onClose(false);
  }, []);

  const initialValues = useMemo(() => {
    return {
      interestReserves: property?.interestReserves || 0,
    };
  }, [property]);

  const onSubmit = (changedValues, { form, values, changes }) => {
    // console.log("changedValues", {
    //   propertyId: property?._id,
    //   loanId: loanDrilldown?._id,
    //   data: values,
    //   onSuccessFn: (response) => {
    //     // console.log("response", response);
    //     dispatch(loanDrilldownSet(response));
    //   },
    // });
    // return;
    updateSubjectProperty({
      propertyId: property?._id,
      loanId: loanDrilldown?._id,
      data: values,
      onSuccessFn: (response) => {
        // console.log("response", response);
        dispatch(loanDrilldownSet(response));
      },
    });
  };

  return (
    <BasicModal
      title="Update Property Interest Reserves"
      description={
        addressLabel(property?.address)
      }
      show={!isNil(property)}
      icon={<HouseOutlined className="thin" />}
      onClose={handleOnClose}
      bodySx={{ p: 3 }}
      maxWidth="xs"
    >
      <RffFormChanges
        submitText="Update Property"
        onSubmit={onSubmit}
        // onCancelText={"Reset"}
        onCancel={handleOnClose}
        initialValues={initialValues}
        loading={loading}
      >
        <RffDollarField name="interestReserves" label="Interest Reserves" />
      </RffFormChanges>
    </BasicModal>
  );
};

const LoanClosingCard = ({ quickFilter }) => {
  const { values } = useFormState();
  const fields = useMemo(() => {
    let baseFieldsStart = [
      {
        name: "closingStatus",
        label: "Closing Status",
        type: "select",
        options: [
          "An issue with the file",
          "Clearing Post Closing Conditions",
          "Documents drafted",
          "Engage Counsel",
          "Executed Loan Docs Received and Under Review",
          "Expecting incoming wire",
          "Funded",
          "Loan docs need to be drafted",
          "Loan docs out for execution",
          "Opened with Counsel",
          "P/C Collateral Cleared",
          "P/C Pending Scans",
          "P/C Scans Received and Under Review",
          "Ready for QC Review",
          "Ready to request wire",
          "Requested wire",
          "Settlement Statement Under Review",
          "SS out for signature",
          "Waiting for incoming wire",
        ],
      },
      {
        name: "closingScheduled",
        label: "Closing Scheduled?",
        type: "booleanCheckbox",
      },
    ];

    if (values?.closingScheduled) {
      baseFieldsStart.push({
        name: "closingScheduledDate",
        label: "Closing Scheduled Date",
        type: "date",
      });
    }

    baseFieldsStart = [
      ...baseFieldsStart,
      {
        name: "fundingShieldStatus",
        label: "Funding Shield Status",
        type: "select",
        options: ["Not Submitted", "Submitted", "Received"],
      },
      {
        name: "fundingShieldRequestedDate",
        label: "Funding Shield Requested Date",
        type: "date",
      },
      {
        name: "originationDate",
        label: "Origination Date",
        type: "date",
      },
      {
        name: "termsAccepted",
        label: "Terms Accepted?",
        type: "booleanCheckbox",
      },
    ];

    if (values?.termsAccepted) {
      baseFieldsStart.push({
        name: "termsAcceptedDate",
        label: "Terms Accepted Date",
        type: "date",
      });
    }

    baseFieldsStart = [
      ...baseFieldsStart,
      {
        name: "wireStatus",
        label: "Wire Status",
        type: "selectToggle",
        options: selectOptionsWireStatus,
      },
      {
        name: "wireSentDate",
        label: "Wire Sent Date",
        type: "date",
      },
    ];

    return baseFieldsStart;
  }, [values?.closingScheduled, values?.termsAccepted]);

  return (
    <FilterableLoanDataCard
      // headerEndContent={<LoanDrilldownButtonMarkLoan type="Closed" />}
      title="Loan Closing"
      fields={fields}
      icon={<HandshakeOutlined className="thin" />}
      quickFilter={quickFilter}
    />
  );
};

const RefinanceInfoCard = ({ quickFilter }) => {
  const fields = useMemo(
    () => [
      {
        name: "refinancingAssetId",
        label: "Refinancing Asset Id",
        type: "text",
      },
      {
        name: "closingAsEntity",
        label: "Closing As Entity?",
        type: "booleanToggle",
      },

      {
        name: "interestCalcType",
        label: "Interest Calc Type",
        type: "select",
        options: selectOptionsInterestCalcType,
      },
      {
        name: "outstandingLoanUpb",
        label: "Outstanding Loan UPB",
        type: "dollar",
      },
      {
        name: "exisitingLender",
        label: "Existing Lender",
        type: "text",
      },
    ],
    [],
  );

  return (
    <FilterableLoanDataCard
      title="Refinance Info"
      fields={fields}
      icon={<CurrencyExchangeOutlined className="thin" />}
      quickFilter={quickFilter}
    />
  );
};

const PrepaymentCard = ({ quickFilter }) => {
  const fields = useMemo(
    () => [
      {
        name: "vomPaymentHistory",
        label: "VOM Payment History",
        // Backend models this as `list` (form_type 'selectMultiple') — a plain
        // single `select` wrote a string here and broke UnderwritingBaseModel
        // validation. Must stay a multi-select so it writes an array.
        type: "selectMultiple",
        options: selectOptionsVomPaymentHistory,
      },
      {
        name: "prepaymentPenaltyStructure",
        label: "Prepayment Structure",
        type: "select",
        options: selectOptionsPrepaymentPenaltyStructure,
      },
    ],
    [],
  );

  return (
    <FilterableLoanDataCard
      title="Prepayment"
      fields={fields}
      icon={<PointOfSaleOutlined className="thin" />}
      quickFilter={quickFilter}
    />
  );
};

const DeadDealCard = ({ quickFilter }) => {
  const fields = useMemo(
    () => [
      {
        name: "dealDeadReason",
        label: "Deal Dead Reason",
        type: "text",
      },
      {
        name: "dealDeadNotes",
        label: "Deal Dead Notes",
        type: "stringMultiline",
      },
    ],
    [],
  );

  return (
    <FilterableLoanDataCard
      title="Dead Deal"
      fields={fields}
      // headerEndContent={<LoanDrilldownButtonMarkLoan type="Dead" />}
      icon={<DoDisturbOutlined className="thin" />}
      quickFilter={quickFilter}
    />
  );
};

const FilterableLoanDataCard = ({
  title,
  fields,
  headerEndContent,
  quickFilter,
  children,
  icon,
}) => {
  return (
    <TitledCard
      variant="h3"
      title={title}
      headerEndContent={headerEndContent}
      icon={icon}
    >
      {isNil(fields) ? null : (
        <LoanDrilldownEditableDataFieldsRenderer
          quickFilter={quickFilter}
          fields={fields}
        />
      )}
      {children}
    </TitledCard>
  );
};

const LoanDrilldownEditableDataFieldsRenderer = ({
  quickFilter,
  fields,
  gridTemplateColumns,
}) => {
  return (
    <DataRowSectionRenderer
      quickFilter={quickFilter}
      fields={fields}
      gridTemplateColumns={gridTemplateColumns}
    />
  );
};

const DataRowSectionRenderer = ({
  quickFilter,
  fields,
  gridTemplateColumns,
}) => {
  const filteredFields = useMemo(() => {
    if (!quickFilter?.trim()) {
      return fields;
    }

    const lowerFilter = quickFilter.toLowerCase();
    return fields.filter(
      (field) =>
        field?.name?.toLowerCase()?.includes(lowerFilter) ||
        field?.label?.toLowerCase()?.includes(lowerFilter),
    );
  }, [quickFilter, fields]);

  if (!filteredFields.length) {
    return <Txt sx={{ opacity: 0.45 }}>No fields found</Txt>;
  }

  return filteredFields.map((f, i) => (
    <RffDataRow
      key={f.name}
      name={f.name}
      label={f.label}
      gridTemplateColumns={gridTemplateColumns || f.gridTemplateColumns}
      disabled={f?.disabled}
      type={f.type}
      topBorder={i === 0}
      options={f?.options}
    />
  ));
};

export default LoanDrilldownDetailsTab;
